import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Cms, PAGE_TYPES } from '@impact/data';
import { Subject } from 'rxjs';
import { switchMap, take, takeUntil } from 'rxjs/operators';
import { HeaderService } from '../core/header.service';
import { NavigationService } from '../navigation/navigation.service';
import { SidePanelService } from '../side-panel/side-panel.service';
import { FeatureDetectionService } from '../utils/helpers/feature-detection.service';

export interface VehiclePageHeaderData {
    id: string;
    modelName: string;
    vehicleName: string;
    vehicleLogo: Cms.Media;
    showTestDriveButton: boolean;
    startingPrice: number;
    type: string;
}

@Component({
    selector: 'app-new-cars-page-header',
    template: `
        <div
            class="scroll-sticky-wrapper"
            #componentRef
            [ngStyle]="{ height: elementHeight }"
            [ngClass]="{ 'scroll-sticky-wrapper--is-sticky': isFixed }"
        >
            <div class="page-navigation-header new-cars-details__page-navigation-header" [style.top]="moveMenuDown ? headerHeight + 'px' : ''">
                <div class="page-navigation-header__wrapper">
                    <div class="page-navigation-header__column-left">
                        <a
                            *ngIf="navigationData$ | async as navigationData"
                            class="page-secondary-navigation__item-link"
                            [routerLink]="navigationData.url"
                        >
                            <img
                                loading="lazy"
                                *ngIf="vehiclePageHeaderData?.vehicleLogo?.src"
                                [src]="vehiclePageHeaderData.vehicleLogo?.src | resolveMediaUrl: { height: 80, format: 'webp' }"
                                [alt]="vehiclePageHeaderData?.vehicleName"
                            />
                            <span class="model-name" *ngIf="!vehiclePageHeaderData?.vehicleLogo?.src">{{ vehiclePageHeaderData?.modelName }}</span>
                        </a>
                    </div>

                    <div class="page-navigation-header__column-center">
                        <app-page-secondary-navigation
                            *ngIf="navigationData$ | async as navigationData"
                            [navigationData]="navigationData"
                        ></app-page-secondary-navigation>
                    </div>

                    <div class="page-navigation-header__column-right">
                        <div class="page-navigation-header__price" *ngIf="startingPrice">
                            <div class="page-navigation-header__model">{{ vehiclePageHeaderData?.vehicleName }}</div>
                            <div class="primary-price">
                                {{ 'car_data.starting_price_from_short' | translate }}{{ startingPrice | defaultcurrency }}
                            </div>
                            <div class="secondary-price" [ngSwitch]="isB2B$ | async">
                                <div class="vat-price" *ngSwitchCase="true">{{ 'generic.vat_excluded' | translate }}</div>
                                <button class="button--as-link" *ngSwitchCase="false" (click)="showLoanSimulator(this.startingPrice)">
                                    <span>{{ 'car_data.starting_price_from_short' | translate }}</span>
                                    <app-vehicle-loan-calculation [objectPrice]="startingPrice"></app-vehicle-loan-calculation>
                                    <div class="button--mini">i</div>
                                </button>
                            </div>
                        </div>

                        <button class="button button--primary page-navigation-header__price-button button--themed" *ngIf="vehiclePageHeaderData.showTestDriveButton" (click)="bookTestDrive()">
                            <i class="page-navigation-header__icon"
                                >${'<svg #steeringWheelIcon class="svg-steering-wheel-icon svg-icon" viewBox="0 0 470 470"><path fill="currentColor" d="M185.3 239.3c0 27.5 22.3 49.7 49.7 49.7s49.7-22.3 49.7-49.7c0-27.5-22.3-49.7-49.7-49.7-27.5-.1-49.7 22.2-49.7 49.7zm222.6 36.4c-.5-9-14.7-21.3-39.3-15.6-18 4.3-42.6 19.4-67.7 44.5-18.5 18.5-35 46.4-35 90.5.4 52.5 144.3-47.4 142-119.4zM61.7 200c-.9 8.5 16.6 7.6 27.5 6.2 34.1-6.2 91.4-35.5 148.7-35.5 40.3 0 83.4 18.9 112.2 27.5 9.9 2.8 24.2 8.1 44.5 9 7.6.5 15.2-1.4 14.7-8.1-2.9-47-63.5-140.3-174.3-140.3-84.3 0-166.7 70.6-173.3 141.2zm1.4 75.7c-2.4 72 142.1 171.9 142.1 119.3 0-44-16.6-72-35-90.5-25.1-24.6-49.2-40.3-67.7-44-24.7-5.6-38.9 6.2-39.4 15.2zM21.9 235c0-117.9 95.2-213.1 213.1-213.1S448.1 117.1 448.1 235 352.9 448.1 235 448.1 21.9 352.9 21.9 235z"/></svg>'}</i
                            >
                            {{ 'buttons.book_test_drive' | translate }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    `,
})
export class NewCarsPageHeaderComponent implements OnInit, OnDestroy, AfterViewInit {
    private readonly unsubscribe = new Subject<void>();

    @ViewChild('componentRef') componentRef: ElementRef;
    @Input() data: Cms.NewVehiclesModelPage | Cms.NewVehiclesModelSubPage;

    startingPrice: number;
    isB2B$ = this.navigationService.isB2B$;
    scrollElement: HTMLElement;
    yPosition: number;
    elementHeight: string;
    isFixed: boolean;
    moveMenuDown: boolean;
    headerHeight: number;
    prevScrollPosition: number;
    vehiclePageHeaderData: VehiclePageHeaderData;

    navigationData$ = this.navigationService.currentUrl$.pipe(
        take(1),
        switchMap(currentUrl => {
            const [noAnchorUrl] = currentUrl.split('#');
            const overviewPageUrl = this.resolveOverviewPageUrl(noAnchorUrl);
            return this.navigationService.getTreeByUrl(overviewPageUrl);
        }),
    );

    constructor(
        private readonly headerService: HeaderService,
        private navigationService: NavigationService,
        private sidepanelService: SidePanelService,
        private featureDetectionService: FeatureDetectionService,
        private cdr: ChangeDetectorRef
    ) {
        this.featureDetectionService.onResize$.pipe(takeUntil(this.unsubscribe)).subscribe((viewport) => {
            if (viewport) {
                // Set a potentially new Y position of component
                this.yPosition = this.componentRef.nativeElement.getBoundingClientRect().top + window.pageYOffset;
            }
        });
    }

    ngOnInit(): void {
        this.vehiclePageHeaderData = {
            ...this.data.vehicleModelData,
            startingPrice: this.data.vehicleModelData.startPrice,
            vehicleName: this.data.vehicleModelData.name,
            vehicleLogo: this.data.vehicleModelData.modelLogo,
        };

        this.startingPrice = this.vehiclePageHeaderData.startingPrice;

        this.headerService
            .getScrollPosition()
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((position: number) => {
                this.handleModelHeaderPosition(position);
            });

        this.headerService
            .getHeaderEvents()
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((height: number) => {
                this.headerHeight = height;
                this.prevScrollPosition = height;
                this.cdr.markForCheck();
            });
    }

    handleModelHeaderPosition(position: number) {
        if (window.scrollY < this.yPosition - this.headerHeight) {
            this.isFixed = false;
            this.moveMenuDown = false;
            this.elementHeight = 'auto';
        } else {
            this.moveMenuDown = position < this.prevScrollPosition;
        }
        if (window.scrollY >= this.yPosition) {
            this.isFixed = true;
            this.elementHeight = `${this.scrollElement.clientHeight}px`;
        }
        this.prevScrollPosition = position;
        this.cdr.markForCheck();
    }

    ngAfterViewInit(): void {
        if (this.componentRef) {
            this.scrollElement = this.componentRef.nativeElement;
            if (this.componentRef.nativeElement.getBoundingClientRect) {
                this.yPosition = this.componentRef.nativeElement.getBoundingClientRect()?.top + window.scrollY;
            }
        }
    }

    showLoanSimulator(price: number): void {
        if (price) {
            this.sidepanelService.showLoanCalculatorSidePanel({
                objectPrice: price,
            });
        }
    }

    bookTestDrive() {
        this.sidepanelService.showBookNewCarTestDriveSidePanel(this.data.vehicleModelData);
    }

    ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    private resolveOverviewPageUrl(currentUrl: string) {
        const isOverviewPage = this.data.type !== PAGE_TYPES.VEHICLE_MODEL_SUBPAGE;

        if (isOverviewPage) {
            return currentUrl;
        } else {
            const lastSlash = currentUrl.lastIndexOf("/");
            return currentUrl.substring(0, lastSlash);
        }
    }
}
